/**
 * ReunionConsultiva — stepper de 4 pasos (mockup-reunion-completo-v4.html).
 *
 * 1. Resumen del cliente — tarjetas read-only del cliente + agenda/notas previas.
 * 2. Cuestionario (split):
 *      izda · Composición flota tipo×uso + Datos generales precargados (compacto).
 *      dcha · CuestionarioDiagnostico embebido (6 ejes, scroll continuo).
 * 3. Foto del antes — FotoReunion.jsx (snapshot · debilidades por eje).
 * 4. Diagnóstico IA — botón generar + resumen IA + abrir informe.
 *
 * Guardado incremental vía PUT /api/reuniones/:id (DEC-001).
 */
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, CheckCircle2, Shield, Fuel, Gauge, Wrench, Monitor, Leaf,
  ArrowLeft, ArrowRight, ChevronRight, FileText, Sparkles, Save, RefreshCw,
  User, Truck, History, X,
} from "lucide-react";
import CuestionarioDiagnostico from "./CuestionarioDiagnostico";
import FotoReunion from "./FotoReunion";
import ComposicionFlota, { calcularTotales } from "./ComposicionFlota";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const authAxios = axios.create();
authAxios.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("mobilsafe_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// Metadata de ejes — JSX renderizado, lo consume CuestionarioDiagnostico tal cual.
const META_EJE = {
  datos_cliente:  { nombre: "Datos del Cliente",        icono: <User className="w-5 h-5" />,    color: "from-slate-500 to-slate-600" },
  datos_flota:    { nombre: "Composición de Flota",     icono: <Truck className="w-5 h-5" />,   color: "from-cyan-500 to-blue-500" },
  seguridad:      { nombre: "Seguridad y Riesgo",       icono: <Shield className="w-5 h-5" />,  color: "from-emerald-500 to-green-500" },
  combustible:    { nombre: "Combustible y Conducción", icono: <Fuel className="w-5 h-5" />,    color: "from-orange-500 to-red-500" },
  operativa:      { nombre: "Operativa y Control",      icono: <Gauge className="w-5 h-5" />,   color: "from-blue-500 to-cyan-500" },
  mantenimiento:  { nombre: "Mantenimiento",            icono: <Wrench className="w-5 h-5" />,  color: "from-amber-500 to-yellow-500" },
  tic:            { nombre: "TIC y Digitalización",     icono: <Monitor className="w-5 h-5" />, color: "from-violet-500 to-purple-500" },
  sostenibilidad: { nombre: "Sostenibilidad y CAES",    icono: <Leaf className="w-5 h-5" />,    color: "from-emerald-500 to-green-500" },
};

// Campos de "Eje 0 · Datos del Cliente" — formulario, no preguntas multi-opción.
const CAMPOS_DATOS_CLIENTE = [
  { k: "interlocutor_nombre", label: "Nombre del interlocutor", type: "text",  placeholder: "Ej: Juan García" },
  { k: "interlocutor_cargo",  label: "Cargo",                   type: "text",  placeholder: "Ej: Director de Flota" },
  { k: "interlocutor_email",  label: "Email",                   type: "email", placeholder: "juan@empresa.com" },
  { k: "interlocutor_tel",    label: "Teléfono",                type: "text",  placeholder: "+34 600 000 000" },
];
const SECTORES_OPCIONES = ["Logística", "Transporte pasajeros", "Distribución", "Construcción", "Servicios", "Otro"];

// Datos generales editables a la izquierda (precargados desde Configuración cuando exista).
const DATOS_GENERALES = [
  { k: "coste_litro",                    label: "Coste combustible",            unidad: "€/L", step: "0.01", min: 0 },
  { k: "pct_multas",                     label: "% Multas anual",               unidad: "%",   step: "0.1",  min: 0 },
  { k: "pct_gastos_admin",               label: "% Gastos administrativos",     unidad: "%",   step: "0.1",  min: 0 },
  { k: "pct_trabajo_admin",              label: "% Trabajo admin. flota",       unidad: "%",   step: "0.1",  min: 0 },
  { k: "pct_coste_flota_facturacion",    label: "% Coste flota / facturación",  unidad: "%",   step: "0.1",  min: 0 },
];

function preguntasBDtoAreas(preguntas = []) {
  const byEje = {};
  for (const p of preguntas) {
    if (p.activa === false) continue;
    if (!byEje[p.eje]) byEje[p.eje] = [];
    byEje[p.eje].push(p);
  }
  const ejesOrdenados = ["seguridad", "combustible", "operativa", "mantenimiento", "tic", "sostenibilidad"]
    .filter((e) => byEje[e]);
  return ejesOrdenados.map((eje) => {
    const meta = META_EJE[eje] || { nombre: eje, icono: <Shield className="w-5 h-5" />, color: "from-slate-500 to-slate-600" };
    return {
      id: eje,
      nombre: meta.nombre,
      icono: meta.icono,
      color: meta.color,
      preguntas: byEje[eje].sort((a, b) => (a.orden || 0) - (b.orden || 0)),
    };
  });
}

function buildEjeDe(preguntasCfg) {
  const ejeDe = {};
  for (const p of preguntasCfg) ejeDe[p.id] = p.eje;
  return ejeDe;
}

function arrayToMap(arr = []) {
  const m = {};
  for (const r of arr) m[r.pregunta_id] = { valor: r.opcion ?? null, nota: r.notas_libre ?? "" };
  return m;
}

function mapToArray(m, ejeDe) {
  return Object.entries(m)
    .filter(([_, v]) => (v.valor !== null && v.valor !== undefined) || (v.nota && v.nota.trim()))
    .map(([pregunta_id, v]) => ({
      pregunta_id,
      eje: ejeDe[pregunta_id] || "otros",
      opcion: v.valor || null,
      notas_libre: v.nota || null,
    }));
}

// Componente principal --------------------------------------------------------

export default function ReunionConsultiva({ reunionId, cliente, onBack, onAbrirInforme, pasoInicial }) {
  const [reunion, setReunion] = useState(null);
  const [paso, setPaso] = useState(1);
  const [respuestas, setRespuestas] = useState({});
  const [areas, setAreas] = useState([]);
  const [preguntasCfg, setPreguntasCfg] = useState([]);
  const [flotaAprox, setFlotaAprox] = useState({});      // dict permisivo en BD
  const [composicion, setComposicion] = useState([]);     // [{tipo_id,uso_id,num_vehiculos,km_anio}]
  const [datosGen, setDatosGen] = useState({});           // {coste_litro, pct_multas, ...}
  const [datosCliente, setDatosCliente] = useState({});   // C1 · Eje 0 · {interlocutor_nombre, cargo, email, tel, sector_reunion, notas_generales}
  const [agenda, setAgenda] = useState("");
  const [loading, setLoading] = useState(true);
  const [generandoIA, setGenerandoIA] = useState(false);
  const [saveState, setSaveState] = useState("idle");
  const [mostrarHistorico, setMostrarHistorico] = useState(false); // BACKLOG-006
  const debounceRef = useRef(null);
  const flotaDebounceRef = useRef(null);
  const skipFirstRef = useRef(true);
  const skipFirstFlotaRef = useRef(true);

  // Carga inicial
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [reunionRes, cuestRes, dgenRes] = await Promise.all([
          authAxios.get(`${API}/reuniones/${reunionId}`),
          authAxios.get(`${API}/configuracion-fase2/cuestionario`).catch(() => ({ data: { preguntas: [] } })),
          authAxios.get(`${API}/configuracion-fase2/datos-generales`).catch(() => ({ data: null })),
        ]);
        const r = reunionRes.data;
        setReunion(r);
        setRespuestas(arrayToMap(r.respuestas_cuestionario || []));
        const fa = r.flota_aproximada || {};
        setFlotaAprox(fa);
        setComposicion(Array.isArray(fa.composicion) ? fa.composicion : []);
        // B4.3 (2026-05-03) · Datos generales precargan desde Configuración global si la
        // reunión todavía no los tiene guardados. Una vez el comercial los teclea, la
        // reunión los persiste y prevalecen sobre los defaults globales.
        const dgen = dgenRes.data || {};
        const dg = {};
        for (const c of DATOS_GENERALES) {
          if (fa[c.k] !== undefined && fa[c.k] !== null && fa[c.k] !== "") {
            dg[c.k] = fa[c.k];
          } else if (dgen[c.k] !== undefined && dgen[c.k] !== null) {
            dg[c.k] = dgen[c.k];
          } else {
            dg[c.k] = "";
          }
        }
        setDatosGen(dg);
        // C1 · Eje 0 · datos del cliente persistidos en flota_aproximada.datos_cliente
        const dc = (fa.datos_cliente && typeof fa.datos_cliente === "object") ? fa.datos_cliente : {};
        // Precargar desde el cliente si los campos están vacíos
        setDatosCliente({
          interlocutor_nombre: dc.interlocutor_nombre || cliente?.contacto_principal?.nombre || "",
          interlocutor_cargo:  dc.interlocutor_cargo  || cliente?.contacto_principal?.cargo  || "",
          interlocutor_email:  dc.interlocutor_email  || cliente?.contacto_principal?.email  || "",
          interlocutor_tel:    dc.interlocutor_tel    || cliente?.contacto_principal?.telefono || "",
          sector_reunion:      dc.sector_reunion      || cliente?.sector || "",
          notas_generales:     dc.notas_generales     || "",
        });
        setAgenda(r.agenda_notas || "");
        const preguntas = cuestRes.data?.preguntas || [];
        setPreguntasCfg(preguntas);
        setAreas(preguntasBDtoAreas(preguntas));

        // IMPL-038 · pasoInicial explícito tiene prioridad sobre auto-arranque
        if (pasoInicial && [2, 3, 4].includes(pasoInicial)) {
          setPaso(pasoInicial);
        } else if (r.diagnostico_ia?.resumen || r.diagnostico_ia?.texto_editado) {
          setPaso(4);
        } else {
          setPaso(2);
        }
      } catch {
        toast.error("Error cargando la reunión");
      }
      setLoading(false);
    })();
  }, [reunionId]);

  // Guardado debounced de respuestas del cuestionario
  useEffect(() => {
    if (loading) return;
    if (skipFirstRef.current) { skipFirstRef.current = false; return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSaveState("saving");
      try {
        const ejeDe = buildEjeDe(preguntasCfg);
        const payload = { respuestas_cuestionario: mapToArray(respuestas, ejeDe) };
        if (reunion?.estado === "borrador" && payload.respuestas_cuestionario.length > 0) {
          payload.estado = "en_curso";
        }
        await authAxios.put(`${API}/reuniones/${reunionId}`, payload);
        setSaveState("saved");
        setTimeout(() => setSaveState("idle"), 1500);
      } catch {
        setSaveState("error");
      }
    }, 500);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [respuestas, loading, reunionId, preguntasCfg, reunion?.estado]);

  // Guardado debounced de flota (composición + datos generales)
  useEffect(() => {
    if (loading) return;
    if (skipFirstFlotaRef.current) { skipFirstFlotaRef.current = false; return; }
    if (flotaDebounceRef.current) clearTimeout(flotaDebounceRef.current);
    flotaDebounceRef.current = setTimeout(async () => {
      setSaveState("saving");
      try {
        const totales = calcularTotales(composicion);
        const payload = {
          flota_aproximada: {
            ...flotaAprox,
            ...datosGen,
            composicion,
            datos_cliente: datosCliente,
            num_vehiculos: totales.num_vehiculos_total,
            km_anio_medio: totales.km_anio_medio,
          },
          estado: reunion?.estado === "borrador" ? "en_curso" : reunion?.estado,
        };
        await authAxios.put(`${API}/reuniones/${reunionId}`, payload);
        setSaveState("saved");
        setTimeout(() => setSaveState("idle"), 1500);
      } catch {
        setSaveState("error");
      }
    }, 600);
    return () => flotaDebounceRef.current && clearTimeout(flotaDebounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composicion, datosGen, datosCliente]);

  // Guarda la agenda al perder foco (no debounce — campo de texto largo)
  async function guardarAgenda(texto) {
    try {
      await authAxios.put(`${API}/reuniones/${reunionId}`, { agenda_notas: texto });
    } catch { /* silencioso */ }
  }

  // Generar diagnóstico IA (paso 4) · soporta regeneración con confirmación
  async function generarDiagnostico(opts = {}) {
    const esRegeneracion = !!opts.regenerar;
    if (esRegeneracion) {
      // eslint-disable-next-line no-alert
      const ok = window.confirm("¿Seguro que quieres regenerar el diagnóstico? El texto actual será sustituido.");
      if (!ok) return;
    }
    setGenerandoIA(true);
    try {
      const ejeDe = buildEjeDe(preguntasCfg);
      const payload = {
        empresa: cliente?.empresa || "Cliente",
        sector: cliente?.sector || "",
        flota: { ...flotaAprox, ...datosGen, composicion },
        respuestas: mapToArray(respuestas, ejeDe),
      };
      const ia = await authAxios.post(`${API}/ia/generar-diagnostico`, payload);
      await authAxios.put(`${API}/reuniones/${reunionId}`, {
        diagnostico_ia: {
          resumen: ia.data?.resumen || null,
          recomendacion: typeof ia.data?.problemas === "object" ? JSON.stringify(ia.data.problemas) : null,
          generado_at: new Date().toISOString(),
        },
        estado: "completada",
      });
      const r = await authAxios.get(`${API}/reuniones/${reunionId}`);
      setReunion(r.data);
      toast.success(esRegeneracion ? "Diagnóstico regenerado" : "Diagnóstico generado");
    } catch {
      toast.error(esRegeneracion
        ? "No se pudo regenerar. Mantenemos el diagnóstico anterior."
        : "Error generando diagnóstico IA");
    }
    setGenerandoIA(false);
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }
  if (!reunion) return null;

  // ----------------------------------------------------------------- LAYOUT

  return (
    <div className="min-h-screen bg-slate-50" data-testid="reunion-stepper">
      <StepperHeader
        paso={paso}
        onBack={onBack}
        onGo={setPaso}
        saveState={saveState}
        cliente={cliente}
      />

      {/* PASO 3 · FOTO DEL ANTES — debilidades por eje + observaciones */}
      {paso === 3 && (
        <FotoReunion
          reunion={{ ...reunion, flota_aproximada: { ...flotaAprox, ...datosGen, composicion }, respuestas_cuestionario: mapToArrayLocal(respuestas, preguntasCfg) }}
          cliente={cliente}
          preguntasCfg={preguntasCfg}
          onGenerarDiagnostico={() => setPaso(4)}
          onGuardarObservaciones={async (texto) => {
            try {
              await authAxios.put(`${API}/reuniones/${reunionId}`, { observaciones_reunion: texto });
              setReunion((r) => r ? { ...r, observaciones_reunion: texto } : r);
            } catch { /* silencioso */ }
          }}
        />
      )}

      {/* PASO 2 · CUESTIONARIO 8 EJES (carrusel único · spec UX-FIXES C1) */}
      {paso === 2 && (
        <main className="max-w-7xl mx-auto px-6 py-6 pb-32" data-testid="paso-2-cuestionario">
          <SectionTitle n={2} titulo="Cuestionario · 8 ejes" sub="datos del cliente · composición de flota · 6 áreas de diagnóstico" />
          <CuestionarioOchoEjes
            cliente={cliente}
            datosCliente={datosCliente}
            setDatosCliente={setDatosCliente}
            composicion={composicion}
            setComposicion={setComposicion}
            datosGen={datosGen}
            setDatosGen={setDatosGen}
            areas={areas}
            respuestas={respuestas}
            setRespuestas={setRespuestas}
          />

          {/* IMPL-034 (2026-05-12) · agenda/notas previas absorbidas aquí desde el paso 1 eliminado */}
          <div className="mt-6 pt-6 border-t border-slate-200">
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2">Notas previas de la reunión</h3>
            <p className="text-xs text-slate-500 mb-2">Qué quieres tratar, qué documentación enviaste antes, asistentes…</p>
            <textarea
              rows={4}
              data-testid="paso-2-agenda"
              defaultValue={agenda}
              onBlur={(e) => { setAgenda(e.target.value); guardarAgenda(e.target.value); }}
              placeholder="Notas opcionales sobre la preparación de la reunión…"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-emerald-500 bg-white"
            />
          </div>

          <div className="mt-6 flex justify-end flex-wrap gap-2">
            <button onClick={() => setPaso(3)} data-testid="paso-2-siguiente"
              className="px-5 py-2 text-sm bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-full font-semibold hover:from-emerald-600 hover:to-green-600 shadow-lg shadow-emerald-500/30 inline-flex items-center gap-2">
              Siguiente: Foto del antes <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </main>
      )}

      {/* PASO 4 · DIAGNÓSTICO */}
      {paso === 4 && (
        <main className="max-w-4xl mx-auto px-6 py-8">
          <section className="bg-white rounded-2xl border p-6" data-testid="paso-4-diagnostico">
            <h2 className="text-lg font-bold mb-1 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-500" /> Diagnóstico IA
            </h2>
            <p className="text-sm text-slate-500 mb-4">
              Análisis de las respuestas del cuestionario, notas por eje y datos de flota.
            </p>

            {!reunion.diagnostico_ia?.resumen && !reunion.diagnostico_ia?.texto_editado ? (
              <div className="p-6 bg-slate-50 rounded-lg text-center">
                <p className="text-sm text-slate-600 mb-4">Aún no se ha generado el diagnóstico para esta reunión.</p>
                <button onClick={generarDiagnostico} disabled={generandoIA} data-testid="paso-4-generar"
                  className="px-4 py-2 text-sm bg-violet-600 text-white rounded-full hover:bg-violet-700 disabled:opacity-50 inline-flex items-center gap-2">
                  {generandoIA ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  Generar diagnóstico IA
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="p-4 bg-violet-50 border border-violet-200 rounded-lg">
                  <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
                    <div className="text-xs font-semibold uppercase text-violet-700">Resumen IA</div>
                    <div className="flex items-center gap-2">
                      {/* BACKLOG-006 (IMPL-032 · 2026-05-12) · histórico de diagnósticos */}
                      {(reunion.diagnostico_ia_historico || []).length > 0 && (
                        <button
                          onClick={() => setMostrarHistorico(true)}
                          data-testid="btn-historico-diagnostico"
                          className="text-xs px-3 py-1 rounded-full border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 inline-flex items-center gap-1.5"
                        >
                          <History className="w-3.5 h-3.5" />
                          Versiones anteriores ({(reunion.diagnostico_ia_historico || []).length})
                        </button>
                      )}
                      <button
                        onClick={() => generarDiagnostico({ regenerar: true })}
                        disabled={generandoIA}
                        data-testid="btn-regenerar-diagnostico"
                        className="text-xs px-3 py-1 rounded-full border border-violet-300 text-violet-700 bg-white hover:bg-violet-100 disabled:opacity-50 inline-flex items-center gap-1.5"
                      >
                        {generandoIA ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                        {generandoIA ? "Regenerando..." : "Regenerar"}
                      </button>
                    </div>
                  </div>
                  <div className="text-sm text-slate-800 prose prose-sm max-w-none prose-p:my-2 prose-strong:text-slate-900 prose-headings:text-slate-900 prose-li:my-0.5 prose-table:my-3 prose-th:bg-violet-100 prose-th:text-slate-800 prose-th:font-bold prose-th:px-3 prose-th:py-2 prose-td:px-3 prose-td:py-2 prose-td:align-top prose-table:border prose-table:border-slate-200 prose-th:border prose-th:border-slate-200 prose-td:border prose-td:border-slate-200">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {reunion.diagnostico_ia?.texto_editado || reunion.diagnostico_ia?.resumen}
                    </ReactMarkdown>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Diagnóstico guardado · estado: {reunion.estado}
                </div>
              </div>
            )}

            <div className="mt-6 flex justify-between flex-wrap gap-2">
              <button onClick={() => setPaso(3)} data-testid="paso-4-anterior"
                className="px-4 py-2 text-sm text-slate-700 border border-slate-300 rounded-full hover:bg-slate-50 inline-flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" /> Volver a la foto
              </button>
              {(reunion.diagnostico_ia?.resumen || reunion.diagnostico_ia?.texto_editado) && onAbrirInforme && (
                <button onClick={() => onAbrirInforme(reunion)} data-testid="paso-4-abrir-informe"
                  className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-full hover:bg-emerald-700 inline-flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Abrir informe completo
                </button>
              )}
              {(reunion.diagnostico_ia?.resumen || reunion.diagnostico_ia?.texto_editado) && !onAbrirInforme && (
                <button onClick={onBack} data-testid="paso-4-volver"
                  className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-full hover:bg-emerald-700">
                  Cerrar reunión
                </button>
              )}
            </div>
          </section>
        </main>
      )}

      {/* BACKLOG-006 (IMPL-032 · 2026-05-12) · modal histórico de diagnósticos · solo lectura */}
      {mostrarHistorico && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setMostrarHistorico(false)}
          data-testid="modal-historico-diagnostico"
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-3 bg-slate-50">
              <div className="flex items-center gap-2 min-w-0">
                <History className="w-5 h-5 text-violet-600 flex-shrink-0" />
                <div className="min-w-0">
                  <h3 className="font-semibold text-slate-900 truncate">Versiones anteriores del diagnóstico IA</h3>
                  <p className="text-xs text-slate-500">
                    {(reunion.diagnostico_ia_historico || []).length} {(reunion.diagnostico_ia_historico || []).length === 1 ? "versión" : "versiones"} previas · solo lectura
                  </p>
                </div>
              </div>
              <button
                onClick={() => setMostrarHistorico(false)}
                data-testid="btn-cerrar-historico"
                className="p-1.5 hover:bg-slate-200 rounded-lg transition flex-shrink-0"
                aria-label="Cerrar"
              >
                <X className="w-5 h-5 text-slate-600" />
              </button>
            </header>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {(reunion.diagnostico_ia_historico || []).length === 0 ? (
                <p className="text-sm text-slate-500 italic text-center py-8">
                  No hay versiones anteriores guardadas.
                </p>
              ) : (
                [...(reunion.diagnostico_ia_historico || [])].reverse().map((d, i, arr) => {
                  const numVersion = arr.length - i;
                  const fechaTxt = d.generado_at
                    ? new Date(d.generado_at).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" })
                    : "Fecha desconocida";
                  return (
                    <article
                      key={i}
                      className="border border-slate-200 rounded-lg p-4 bg-slate-50/50"
                      data-testid={`historico-version-${numVersion}`}
                    >
                      <header className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] uppercase tracking-wider font-bold text-violet-700 bg-violet-100 px-2 py-0.5 rounded-full">
                            v{numVersion}
                          </span>
                          <span className="text-xs text-slate-500">{fechaTxt}</span>
                        </div>
                        {d.texto_editado && (
                          <span className="text-[10px] uppercase tracking-wider text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full font-semibold">
                            con ediciones manuales
                          </span>
                        )}
                      </header>
                      <div className="text-sm text-slate-800 prose prose-sm max-w-none prose-p:my-2 prose-strong:text-slate-900 prose-headings:text-slate-900 prose-li:my-0.5">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {d.texto_editado || d.resumen || "(sin contenido)"}
                        </ReactMarkdown>
                      </div>
                    </article>
                  );
                })
              )}
            </div>
            <footer className="px-5 py-3 border-t border-slate-200 bg-slate-50 text-[11px] text-slate-500 italic">
              El texto editado manualmente tiene precedencia sobre el resumen IA original.
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ Sub-componentes ============

function StepperHeader({ paso, onBack, onGo, saveState, cliente }) {
  // IMPL-034 (2026-05-12) · paso 1 "Resumen" eliminado · stepper fijo de 3 pasos · sin acciones contextuales
  const pasos = [
    { n: 2, label: "Cuestionario" },
    { n: 3, label: "Foto antes" },
    { n: 4, label: "Diagnóstico" },
  ];
  return (
    <header className="sticky top-0 z-10 border-b border-white/10" style={{ background: '#ec561c' }} data-testid="reunion-stepper-header">
      {/* IMPL-029 (2026-05-06) · Header 3 columnas: izda atrás · centro pasos grandes · dcha cliente */}
      <div className="max-w-7xl mx-auto px-6 py-3 grid grid-cols-[auto_1fr_auto] items-center gap-4">

        {/* IZDA · botón atrás */}
        <button onClick={onBack} data-testid="reunion-stepper-back"
          className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10">
          <ArrowLeft className="w-5 h-5" />
        </button>

        {/* CENTRO · pasos grandes */}
        <nav className="flex flex-wrap items-center justify-center gap-1.5" data-testid="reunion-stepper-nav">
          {pasos.map((p, i) => (
            <React.Fragment key={p.n}>
              <button onClick={() => onGo(p.n)}
                data-testid={`stepper-paso-${p.n}`}
                className={`text-sm md:text-base px-3 md:px-5 py-1.5 md:py-2 rounded-full transition ${
                  p.n === paso
                    ? "bg-white text-[#ec561c] font-bold shadow"
                    : p.n < paso
                      ? "bg-white/20 text-white hover:bg-white/30 font-semibold"
                      : "bg-white/10 text-white/50 hover:bg-white/20 font-medium"
                }`}>
                <span className="font-bold">{p.n}</span><span className="hidden md:inline">·{p.label}</span>
              </button>
              {i < pasos.length - 1 && <span className="hidden md:inline text-white/30 text-xs">›</span>}
            </React.Fragment>
          ))}
        </nav>

        {/* DCHA · cliente + savestate */}
        <div className="flex items-center gap-3 justify-end">
          <div className="hidden md:flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0 text-right">
              <p className="text-[10px] uppercase tracking-widest text-white/60 font-bold leading-none">Reunión consultiva</p>
              <h1 className="text-sm font-black tracking-tight text-white truncate" data-testid="stepper-cliente-nombre">{cliente?.empresa || "Cliente"}</h1>
            </div>
          </div>
          <SaveIndicator state={saveState} inline onOrange />
        </div>
      </div>
    </header>
  );
}

function SectionTitle({ n, titulo, sub }) {
  // FIX-UX-1 (2026-05-04) · oculto en md+ porque el stepper ya muestra paso+nombre
  return (
    <div className="md:hidden flex items-center gap-3 mb-5 flex-wrap">
      <span className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-green-500 rounded-full flex items-center justify-center text-white font-black text-sm shadow-lg shadow-emerald-500/30">{n}</span>
      <h2 className="text-2xl font-black tracking-tight text-slate-900">{titulo}</h2>
      <span className="text-xs text-slate-500">· {sub}</span>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
      <h3 className="text-[11px] uppercase tracking-widest text-emerald-600 font-bold mb-3">{title}</h3>
      {children}
    </section>
  );
}

function KV({ k, v }) {
  return (
    <div>
      <span className="text-xs text-slate-500">{k}</span>
      <div className="font-semibold">{v || <span className="italic text-slate-400">—</span>}</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-slate-500">{k}</span>
      <span className="font-semibold text-right">{v}</span>
    </div>
  );
}

function SaveIndicator({ state, inline = false, onOrange = false }) {
  if (state === "idle") return null;
  const cfg = onOrange ? {
    saving: { bg: "rgba(255,255,255,0.15)", fg: "#ffffff", txt: "Guardando…", Icon: Loader2, spin: true },
    saved:  { bg: "rgba(255,255,255,0.2)",  fg: "#ffffff", txt: "Guardado",  Icon: CheckCircle2 },
    error:  { bg: "rgba(254,202,202,0.25)", fg: "#fff5f5", txt: "Error",     Icon: Save },
  }[state] : {
    saving: { bg: "#dbeafe", fg: "#1e40af", txt: "Guardando…", Icon: Loader2, spin: true },
    saved:  { bg: "#d1fae5", fg: "#065f46", txt: "Guardado",  Icon: CheckCircle2 },
    error:  { bg: "#fee2e2", fg: "#991b1b", txt: "Error",     Icon: Save },
  }[state];
  if (!cfg) return null;
  const Icon = cfg.Icon;
  return (
    <div
      className={`${inline ? "ml-auto" : "fixed top-20 right-6 z-[60]"} px-3 py-1 rounded-full text-xs font-medium shadow-sm flex items-center gap-1`}
      data-testid="reunion-save-indicator"
      style={{ background: cfg.bg, color: cfg.fg }}
    >
      <Icon className={`w-3 h-3 ${cfg.spin ? "animate-spin" : ""}`} /> {cfg.txt}
    </div>
  );
}

// Wrapper de CuestionarioDiagnostico SIN su propio header / footer / botones de Continuar.
// Reusa la lógica de áreas + preguntas pero deja los CTAs al stepper exterior.
function CuestionarioOchoEjes({
  cliente, datosCliente, setDatosCliente,
  composicion, setComposicion,
  datosGen, setDatosGen,
  areas, respuestas, setRespuestas,
}) {
  // Ejes fijos 0 y 1 + 6 áreas configurables
  const ejesFijos = [
    { id: "datos_cliente", ...META_EJE.datos_cliente },
    { id: "datos_flota",   ...META_EJE.datos_flota },
  ];
  const ejes = [...ejesFijos, ...areas];
  const [activeIdx, setActiveIdx] = useState(0);
  const ejeActivo = ejes[activeIdx];

  // Progreso por eje
  function progresoEje(eje) {
    if (eje.id === "datos_cliente") {
      const total = CAMPOS_DATOS_CLIENTE.length + 1; // +sector
      const ok =
        CAMPOS_DATOS_CLIENTE.filter((c) => (datosCliente[c.k] || "").toString().trim()).length +
        (datosCliente.sector_reunion ? 1 : 0);
      return Math.round((ok / total) * 100);
    }
    if (eje.id === "datos_flota") {
      const totalFilas = composicion.length;
      const filasOk = composicion.filter((f) => f.tipo_id && f.uso_id).length;
      return totalFilas === 0 ? 0 : Math.round((filasOk / totalFilas) * 100);
    }
    if (!eje.preguntas?.length) return 0;
    const respondidas = eje.preguntas.filter((p) => respuestas[p.id]?.valor).length;
    return Math.round((respondidas / eje.preguntas.length) * 100);
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm" data-testid="cuestionario-8-ejes">
      {/* Carrusel horizontal de 8 ejes (sticky justo debajo del header naranja sticky · z-20 para superponerse al contenido) */}
      <div className="px-4 py-3 border-b border-slate-100 bg-white sticky top-16 z-20 rounded-t-2xl" data-testid="ejes-carrusel-sticky">
        <div className="flex items-stretch gap-1.5 overflow-x-auto pb-1">
          {ejes.map((eje, idx) => {
            const isActive = idx === activeIdx;
            const prog = progresoEje(eje);
            const isComplete = prog === 100;
            const isInProgress = prog > 0 && prog < 100;
            // const isPending = prog === 0;
            // IMPL-029 · 4 estados visuales (activo · completado · en progreso · pendiente)
            let cardCls, iconCls, textCls, barTrackCls, barFillCls;
            if (isActive) {
              cardCls = `bg-white border-2 border-[#ec561c] shadow-[0_0_0_3px_rgba(236,86,28,0.12),0_3px_10px_rgba(236,86,28,0.15)] -translate-y-0.5${isComplete ? "" : ""}`;
              iconCls = `bg-gradient-to-br ${eje.color} text-white shadow`;
              textCls = "text-[#ec561c] font-bold";
              barTrackCls = isComplete ? "bg-emerald-100" : (isInProgress ? "bg-amber-100" : "bg-slate-200");
              barFillCls = isComplete ? "bg-emerald-500" : (isInProgress ? "bg-amber-400" : "bg-slate-400");
            } else if (isComplete) {
              cardCls = "bg-emerald-50 border-2 border-emerald-300";
              iconCls = "bg-gradient-to-br from-emerald-400 to-green-500 text-white shadow shadow-emerald-500/40";
              textCls = "text-emerald-700 font-semibold";
              barTrackCls = "bg-emerald-100";
              barFillCls = "bg-emerald-500";
            } else if (isInProgress) {
              cardCls = "bg-amber-50 border-2 border-amber-200";
              iconCls = "bg-gradient-to-br from-amber-300 to-orange-400 text-white shadow";
              textCls = "text-amber-800 font-semibold";
              barTrackCls = "bg-amber-100";
              barFillCls = "bg-amber-400";
            } else {
              cardCls = "bg-slate-50 border-2 border-slate-200";
              iconCls = "bg-slate-200 text-slate-400";
              textCls = "text-slate-400 font-medium";
              barTrackCls = "bg-slate-200";
              barFillCls = "bg-slate-400";
            }
            return (
              <button
                key={eje.id}
                onClick={() => setActiveIdx(idx)}
                data-testid={`eje-btn-${eje.id}`}
                className={`relative flex-1 min-w-[120px] flex flex-col items-center gap-1.5 px-3 py-2.5 rounded-xl transition ${cardCls}`}
              >
                {isComplete && !isActive && (
                  <span className="absolute top-1 right-1 w-3.5 h-3.5 bg-emerald-600 text-white rounded-full flex items-center justify-center text-[8px] font-bold leading-none" data-testid={`eje-check-${eje.id}`}>
                    ✓
                  </span>
                )}
                <div className={`w-9 h-9 rounded-full flex items-center justify-center ${iconCls}`}>
                  {eje.icono}
                </div>
                <span className={`text-[11px] text-center leading-tight ${textCls}`}>
                  {idx}. {eje.nombre}
                </span>
                <div className={`w-full h-1 rounded-full overflow-hidden ${barTrackCls}`}>
                  <div className={`h-full transition-all ${barFillCls}`} style={{ width: `${prog}%` }} />
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Contenido del eje activo */}
      <div className="p-5 max-h-[70vh] overflow-y-auto">
        {ejeActivo.id === "datos_cliente" && (
          <Eje0DatosCliente datos={datosCliente} setDatos={setDatosCliente} cliente={cliente} />
        )}
        {ejeActivo.id === "datos_flota" && (
          <Eje1DatosFlota
            composicion={composicion} setComposicion={setComposicion}
            datosGen={datosGen} setDatosGen={setDatosGen}
          />
        )}
        {ejeActivo.id !== "datos_cliente" && ejeActivo.id !== "datos_flota" && (
          <EjeCuestionario eje={ejeActivo} respuestas={respuestas} setRespuestas={setRespuestas} />
        )}
      </div>

      {/* Navegación entre ejes */}
      <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
        <button
          onClick={() => setActiveIdx(Math.max(0, activeIdx - 1))}
          disabled={activeIdx === 0}
          data-testid="eje-anterior"
          className="px-4 py-2 text-sm text-slate-700 border border-slate-300 rounded-lg hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" /> Eje anterior
        </button>
        <span className="text-xs text-slate-500">{activeIdx + 1} / {ejes.length}</span>
        <button
          onClick={() => setActiveIdx(Math.min(ejes.length - 1, activeIdx + 1))}
          disabled={activeIdx === ejes.length - 1}
          data-testid="eje-siguiente"
          className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-2"
        >
          Siguiente eje <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function Eje0DatosCliente({ datos, setDatos, cliente }) {
  const update = (k, v) => setDatos({ ...datos, [k]: v });
  return (
    <div className="space-y-4" data-testid="eje-datos-cliente-content">
      <div>
        <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <User className="w-5 h-5 text-slate-500" /> Datos del Cliente
        </h3>
        <p className="text-sm text-slate-500 mt-0.5">
          Quién ha venido a la reunión y datos básicos del cliente {cliente?.empresa ? `· ${cliente.empresa}` : ""}.
        </p>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {CAMPOS_DATOS_CLIENTE.map((c) => (
          <label key={c.k} className="block">
            <span className="text-xs font-medium text-slate-600">{c.label}</span>
            <input
              type={c.type}
              value={datos[c.k] || ""}
              onChange={(e) => update(c.k, e.target.value)}
              placeholder={c.placeholder}
              data-testid={`datos-cliente-${c.k}`}
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
            />
          </label>
        ))}
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Sector</span>
          <select
            value={datos.sector_reunion || ""}
            onChange={(e) => update("sector_reunion", e.target.value)}
            data-testid="datos-cliente-sector"
            className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 bg-white"
          >
            <option value="">— Selecciona sector —</option>
            {SECTORES_OPCIONES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </div>
      <label className="block">
        <span className="text-xs font-medium text-slate-600">Notas generales de la reunión</span>
        <textarea
          rows={3}
          value={datos.notas_generales || ""}
          onChange={(e) => update("notas_generales", e.target.value)}
          placeholder="Cualquier dato relevante del cliente que no encaje en los otros ejes..."
          data-testid="datos-cliente-notas"
          className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
        />
      </label>
    </div>
  );
}

function Eje1DatosFlota({ composicion, setComposicion, datosGen, setDatosGen }) {
  return (
    <div className="space-y-4" data-testid="eje-datos-flota-content">
      <div>
        <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <Truck className="w-5 h-5 text-cyan-600" /> Composición de Flota
        </h3>
        <p className="text-sm text-slate-500 mt-0.5">
          Tipos × usos de los vehículos del cliente y datos generales para los cálculos del informe.
        </p>
      </div>
      <ComposicionFlota filas={composicion} onChange={setComposicion} />
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4" data-testid="datos-generales">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-[11px] uppercase tracking-widest text-emerald-600 font-bold">Datos generales</h4>
          <span className="text-[10px] text-slate-500">precargados · editables</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {DATOS_GENERALES.map((c) => (
            <label key={c.k} className="block">
              <span className="text-xs text-slate-500">{c.label}</span>
              <div className="flex items-center mt-0.5">
                <input
                  type="number"
                  step={c.step}
                  min={c.min}
                  value={datosGen[c.k] ?? ""}
                  onChange={(e) => setDatosGen({ ...datosGen, [c.k]: e.target.value === "" ? "" : Number(e.target.value) })}
                  data-testid={`dg-${c.k}`}
                  className="w-full px-2 py-1.5 border border-slate-200 rounded-l text-sm font-semibold focus:outline-none focus:border-emerald-500 bg-white"
                />
                <span className="text-xs px-2 py-1.5 bg-white border border-l-0 border-slate-200 rounded-r text-slate-500">{c.unidad}</span>
              </div>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

function EjeCuestionario({ eje, respuestas, setRespuestas }) {
  const set = (preguntaId, patch) => setRespuestas({
    ...respuestas,
    [preguntaId]: { ...(respuestas[preguntaId] || {}), ...patch },
  });
  return (
    <div className="space-y-4" data-testid={`eje-${eje.id}-content`}>
      <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br ${eje.color} text-white shadow`}>
          {eje.icono}
        </div>
        <div>
          <h3 className="text-lg font-bold text-slate-900">{eje.nombre}</h3>
          <p className="text-xs text-slate-500">{eje.preguntas?.length || 0} preguntas</p>
        </div>
      </div>
      <div className="space-y-2.5">
        {(eje.preguntas || []).map((pregunta, idx) => {
          const r = respuestas[pregunta.id] || {};
          const respondida = !!r.valor;
          // IMPL-029 FIX 3 (2026-05-06) · 2 estados: respondida vs sin-responder · referencia: mockup-cuestionario-completo.html
          const cardCls = respondida
            ? "border-2 border-emerald-500 bg-emerald-50"
            : "border-2 border-slate-300 bg-slate-50";
          const leftCls = respondida
            ? "bg-emerald-50 md:border-r-2 md:border-emerald-200 md:border-l-4 md:border-l-emerald-500"
            : "bg-slate-50 md:border-r-2 md:border-slate-300";
          const rightCls = respondida ? "bg-emerald-50/80" : "bg-slate-100";
          const numCls = respondida
            ? "bg-[#0a6640] text-white"
            : "bg-slate-200 text-slate-500 border border-slate-300";
          return (
            <div key={pregunta.id} className={`rounded-xl overflow-hidden transition-all ${cardCls}`}>
              <div className="md:grid md:grid-cols-2 md:items-stretch">
                <div className={`p-4 flex flex-col gap-2.5 ${leftCls}`}>
                  <div className="flex items-start gap-2.5">
                    <span className={`w-[22px] h-[22px] rounded-full flex items-center justify-center text-[11px] font-mono font-bold flex-shrink-0 mt-0.5 ${numCls}`}>
                      {idx + 1}
                    </span>
                    <p className="text-[13px] font-semibold leading-snug text-[#1e3a5f] flex-1">
                      {pregunta.texto}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(pregunta.opciones || []).map((opcion, optIdx) => {
                      // BACKLOG-009 (IMPL-032 · 2026-05-12) · resaltar opciones marcadas como debilidad
                      const esDebilidad = (pregunta.opciones_debilidad || []).includes(opcion);
                      const seleccionada = r.valor === opcion;
                      let cls;
                      if (seleccionada && esDebilidad) {
                        cls = "bg-rose-500 text-white border-rose-500 shadow";
                      } else if (seleccionada) {
                        cls = "bg-emerald-500 text-white border-emerald-500 shadow";
                      } else if (esDebilidad) {
                        cls = "bg-amber-50 border-amber-300 text-amber-800 hover:border-rose-400 hover:bg-amber-100";
                      } else {
                        cls = "bg-white border-slate-200 text-slate-600 hover:border-emerald-400 hover:text-emerald-700";
                      }
                      return (
                        <button
                          key={optIdx}
                          onClick={() => set(pregunta.id, { valor: opcion })}
                          data-testid={`opcion-${pregunta.id}-${optIdx}`}
                          data-debilidad={esDebilidad ? "1" : "0"}
                          title={esDebilidad ? "Opción marcada como debilidad por configuración" : undefined}
                          className={`px-3 py-1 rounded-full text-[11px] font-semibold border-[1.5px] transition ${cls}`}
                        >
                          {esDebilidad && !seleccionada && <span className="mr-1 text-rose-500">●</span>}
                          {opcion}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className={`p-4 flex ${rightCls}`}>
                  <textarea
                    value={r.nota || ""}
                    onChange={(e) => set(pregunta.id, { nota: e.target.value })}
                    placeholder="Notas adicionales (opcional). Pega aquí lo que el cliente cuenta y no encaja en las opciones…"
                    data-testid={`nota-${pregunta.id}`}
                    rows={3}
                    className="w-full min-h-[70px] md:h-full px-3 py-2 border border-slate-200 rounded-lg text-xs bg-white/70 resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                  />
                </div>
              </div>
            </div>
          );
        })}
        {(!eje.preguntas || eje.preguntas.length === 0) && (
          <p className="text-sm text-slate-400 italic text-center py-8">Sin preguntas configuradas para este eje.</p>
        )}
      </div>
    </div>
  );
}

// Helper local para construir el array de respuestas que espera FotoReunion.
function mapToArrayLocal(respuestas, preguntasCfg) {
  const ejeDe = {};
  for (const p of preguntasCfg) ejeDe[p.id] = p.eje;
  return Object.entries(respuestas)
    .filter(([_, v]) => (v.valor !== null && v.valor !== undefined) || (v.nota && v.nota.trim()))
    .map(([pregunta_id, v]) => ({
      pregunta_id,
      eje: ejeDe[pregunta_id] || "otros",
      opcion: v.valor || null,
      notas_libre: v.nota || null,
    }));
}
